import { listAiConfigurationsKeys, useCreateAiConfigurationMutation } from '@lokalise/shared'
import type {
  CreateAiConfigsRequestBody,
  CreateAiConfigsResponseBody,
  ListAiConfigsResponseBody,
} from '@lokalise/translation-api-schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../utils/api'
import { useFetchAiPermissions } from './useFetchAiPermissions'
import { useFetchAiProfiles } from './useFetchAiProfiles'

// Helper type since the type is really long - this is needed for linting
type SuccessfulCreate = PromiseFulfilledResult<CreateAiConfigsResponseBody>

export const useInitialiseProfiles = () => {
  const teamId = getTeamIdFromSession()
  const queryKey = useMemo(() => listAiConfigurationsKeys.teamAiConfigurationsKey(teamId), [teamId])
  const queryClient = useQueryClient()
  const defaultProfilesInitialised = useRef(false)

  const { profiles, isPending: isLoadingAiConfigs, isSuccess } = useFetchAiProfiles()

  const {
    permissions,
    isPending: isLoadingAiPermissions,
    isSuccess: isAiPermissionsSuccess,
  } = useFetchAiPermissions()

  const create = useCreateAiConfigurationMutation(
    appHttpClient,
    authenticatedPublicApiHeaderBuilder,
    {
      teamId,
    },
  )
  const { mutateAsync: createAiConfig, isPending: isCreatingAiConfig } = create

  // First time user visits page, create a TM config entry
  useEffect(() => {
    if (
      isLoadingAiConfigs ||
      isCreatingAiConfig ||
      isLoadingAiPermissions ||
      !isAiPermissionsSuccess ||
      !isSuccess ||
      defaultProfilesInitialised.current
    ) {
      return
    }

    const updates: Promise<CreateAiConfigsResponseBody>[] = []

    const shouldCreateBaseProfile = profiles?.staticProfile === undefined
    const shouldCreateTMProfile =
      permissions?.predefinedProfile.canView && profiles?.predefinedProfile === undefined

    // Ensure that a default static config has been created
    if (shouldCreateBaseProfile) {
      updates.push(createAiConfig(makeDefaultStaticConfigOptions()))
    }

    if (shouldCreateTMProfile) {
      updates.push(createAiConfig(makeDefaultTmConfigOptions()))
    }

    // If they have both already been created, don't do anything
    if (!updates.length) {
      return
    }

    // We only want to send this once, but to need to have the deps array on the effect
    // to handle loading states and other such logic.
    defaultProfilesInitialised.current = true

    ;(async () => {
      const result = await Promise.allSettled(updates)

      const data = result
        .filter((res): res is SuccessfulCreate => res.status === 'fulfilled')
        .map((res) => res.value.data)

      queryClient.setQueryData<ListAiConfigsResponseBody>(queryKey, (cache) =>
        !cache ? { data } : { data: [...data, ...cache.data] },
      )
    })()
  }, [
    createAiConfig,
    profiles,
    isSuccess,
    isLoadingAiConfigs,
    isCreatingAiConfig,
    permissions,
    isLoadingAiPermissions,
    isAiPermissionsSuccess,
    queryClient,
    queryKey,
  ])

  return create
}

const makeDefaultStaticConfigOptions = (): CreateAiConfigsRequestBody => ({
  name: 'Base AI profile',
  is_default: true,
  is_static: true,
})

const makeDefaultTmConfigOptions = (): CreateAiConfigsRequestBody => ({
  name: 'AI profile - TM',
  is_default: false,
  config: {
    rag: {
      type: 'tm',
      created_after: null,
      source_languages: [],
      target_languages: [],
      tags: [],
    },
  },
})
